import React, { useState } from 'react';
import { Shield, Mail, Loader2, Eye, EyeOff } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Alert, AlertDescription } from '../ui/alert';
import GovLoginIntegration from './GovLoginIntegration';
import ApiService from '../../services/api';
import MAILogo from '../ui/MAILogo';

const LoginForm = ({ onLoginSuccess, onSwitchToSignup }) => {
  const [showGovLogin, setShowGovLogin] = useState(true);
  const [formData, setFormData] = useState({
    militaryId: '',
    email: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLocalLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // For demo purposes, try to find user by military ID or email
      let user = null;
      
      if (formData.militaryId) {
        try {
          user = await ApiService.getUserByMilitaryId(formData.militaryId);
        } catch (findErr) {
          console.error('User lookup by military ID failed:', findErr);
        }
      }
      
      if (!user && formData.email) {
        // Try to find user by email - for demo purposes, use the created user
        try {
          const allUsers = await ApiService.getUsers();
          user = allUsers.find(u => u.email === formData.email || u.military_id === formData.militaryId);
        } catch (error) {
          console.error('Error fetching users:', error);
        }
      }
      
      if (!user) {
        throw new Error('User not found. Please check your credentials or create a new account.');
      }
      
      // Mark as local authentication and ensure we use the database user ID
      user.gov_authenticated = false;
      user.auth_method = 'local';
      
      onLoginSuccess(user);
      
    } catch (error) {
      console.error('Login error:', error);
      setError(error.response?.data?.detail || error.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGovLoginSuccess = (govUserData) => {
    onLoginSuccess(govUserData);
  };

  const handleFallbackToLocal = () => {
    setShowGovLogin(false);
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    const authUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    window.location.href = authUrl;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-2xl border-0 bg-white/95 backdrop-blur-sm">
        <CardHeader className="text-center bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-t-lg">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
              <MAILogo className="w-8 h-8" />
            </div>
            <div>
              <CardTitle className="text-2xl font-bold">Access MilConnect AI</CardTitle>
              <p className="text-blue-100">Choose your preferred sign-in method</p>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="p-8">
          {showGovLogin ? (
            <GovLoginIntegration 
              onGovLoginSuccess={handleGovLoginSuccess}
              onFallbackToLocal={handleFallbackToLocal}
            />
          ) : (
            <>
              {error && (
                <Alert className="border-red-200 bg-red-50">
                  <AlertDescription className="text-red-800">{error}</AlertDescription>
                </Alert>
              )}

              {/* OAuth Login Options */}
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-gray-700 text-center mb-4">Sign in with your account</h3>
                
                {/* Google Login */}
                <Button
                  onClick={handleGoogleLogin}
                  disabled={loading}
                  variant="outline"
                  className="w-full py-3 border-2 border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all duration-200"
                >
                  <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continue with Google
                </Button>

                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-gray-500">or</span>
                  </div>
                </div>
              </div>

              {/* Military/Local Login Form */}
              <form onSubmit={handleLocalLogin} className="space-y-4">
                <div className="space-y-4">
                  <div>
                    <label htmlFor="militaryId" className="block text-sm font-medium text-gray-700 mb-1">
                      Military ID
                    </label>
                    <Input
                      id="militaryId"
                      type="text"
                      placeholder="Enter Military ID"
                      value={formData.militaryId}
                      onChange={(e) => setFormData({ ...formData, militaryId: e.target.value })}
                      className="w-full"
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address
                    </label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="Enter Email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full"
                      disabled={loading}
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading || (!formData.militaryId && !formData.email)}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3"
                >
                  {loading ? 'Signing In...' : 'Access Local Account'}
                </Button>
              </form>
                
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => setShowGovLogin(true)}
                  className="text-blue-600 hover:text-blue-700 font-medium text-sm"
                >
                  ← Back to Government Login
                </button>
              </div>
              
              <div className="text-center mt-6">
                <p className="text-gray-600">
                  Don't have an account?{' '}
                  <button
                    type="button"
                    onClick={onSwitchToSignup}
                    className="text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Create Account
                  </button>
                </p>
              </div>

              <div className="text-center pt-4 border-t border-gray-200">
                <p className="text-xs text-gray-500">
                  Secure • Encrypted • Government Authorized
                </p>
              </div>
            </>
          )}
        </CardContent>
        </Card>
      </div>
    );
  };

  export default LoginForm;